/**
 * Push notifications for the iOS build.
 *
 * Uses @capacitor-firebase/messaging rather than @capacitor/push-notifications:
 * on iOS the latter hands back the raw APNs device token, and the backend sends
 * through firebase-admin, which addresses devices by FCM registration token.
 * Registering an APNs token with /api/devices/register produces a device row that
 * never receives anything.
 *
 * Two things here are easy to get wrong and expensive to get wrong:
 *
 *   COLD START DEEP LINKS. Tapping a notification while the app is not running
 *   fires `notificationActionPerformed` during launch — before React Router has
 *   mounted, so there is nothing to navigate. The link is buffered in
 *   `pendingDeepLink` and the app drains it with `consumePendingDeepLink()` (or
 *   `setDeepLinkHandler`, which drains as a side effect) once the router is up.
 *   Skipping the buffer means every cold-start tap lands on the dashboard.
 *
 *   PERMISSION TIMING. iOS shows the system push alert exactly once per install.
 *   `enablePush()` is therefore never called at launch — it is called after login,
 *   from a priming step (`shouldPrimeForPush()` / `dismissPriming()`) that explains
 *   the value first. A denial at first launch is permanent until the user goes
 *   digging in Settings.
 */
import { App } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import axios from 'axios';

import { isNative } from '../config/platform';

export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

export interface PushRegistration {
  status: 'registered' | 'denied' | 'unsupported' | 'error';
  token?: string;
}

/** Re-ask a user who declined the priming step, but not for a month. */
export const PRIMING_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

const PRIMING_DISMISSED_KEY = '6love.push.primingDismissedAt';

let pendingDeepLink: string | null = null;
let deepLinkHandler: ((link: string) => void) | null = null;
let listenersBound = false;
let registeredToken: string | null = null;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Deep links must be same-app paths. `//evil.example` is a protocol-relative URL
 * that react-router would happily treat as a path but a raw assignment would not —
 * rejecting it here keeps the payload from ever steering navigation off-app.
 */
export function isSafeDeepLink(link: unknown): link is string {
  return typeof link === 'string' && link.startsWith('/') && !link.startsWith('//');
}

/** Pulls `data.deep_link` out of a notification event (see push_service.send_push). */
export function extractDeepLink(event: unknown): string | null {
  const data = (event as { notification?: { data?: unknown } } | null)?.notification?.data;
  if (!data || typeof data !== 'object') return null;
  const link = (data as { deep_link?: unknown }).deep_link;
  return isSafeDeepLink(link) ? link : null;
}

/** Capacitor's PermissionState has four values; we only care about three. */
export function normalizePermission(receive: unknown): PushPermission {
  if (receive === 'granted') return 'granted';
  if (receive === 'denied') return 'denied';
  return 'prompt';
}

/**
 * Reads the iOS version out of the WebView user agent, e.g.
 * "...(iPhone; CPU iPhone OS 17_4 like Mac OS X)..." -> "17.4". Used purely as
 * reporting metadata on the device row; @capacitor/device is not installed and is
 * not worth adding for one string.
 */
export function parseIosVersion(userAgent: string): string | null {
  const match = /OS (\d+)[._](\d+)(?:[._](\d+))?\s+like Mac OS X/.exec(userAgent);
  if (!match) return null;
  return [match[1], match[2], match[3]].filter(Boolean).join('.');
}

/** True when enough time has passed to show the priming step again. */
export function isPrimingCooledDown(dismissedAt: number | null, now: number): boolean {
  return dismissedAt === null || now - dismissedAt >= PRIMING_COOLDOWN_MS;
}

// ---------------------------------------------------------------------------
// Deep link buffering
// ---------------------------------------------------------------------------

/**
 * Returns the deep link from a notification tap that arrived before the router
 * was ready, clearing it. Call once, right after the router mounts.
 */
export function consumePendingDeepLink(): string | null {
  const link = pendingDeepLink;
  pendingDeepLink = null;
  return link;
}

/**
 * Registers the live navigation callback. Draining the buffer here means the app
 * only needs this one line — a cold-start tap and a warm tap take the same path.
 */
export function setDeepLinkHandler(handler: ((link: string) => void) | null): void {
  deepLinkHandler = handler;
  if (!handler) return;
  const buffered = consumePendingDeepLink();
  if (buffered) handler(buffered);
}

function routeDeepLink(link: string): void {
  if (deepLinkHandler) {
    deepLinkHandler(link);
  } else {
    pendingDeepLink = link;
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

async function describeDevice(): Promise<{ app_version: string | null; os_version: string | null }> {
  let appVersion: string | null = null;
  try {
    const info = await App.getInfo();
    appVersion = info?.build ? `${info.version} (${info.build})` : (info?.version ?? null);
  } catch {
    // Web, or the App plugin is unavailable — metadata only, keep going.
  }
  const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return { app_version: appVersion, os_version: parseIosVersion(userAgent) };
}

/** Upserts the token on the backend. /api/devices/register is idempotent. */
async function syncToken(token: string | undefined | null): Promise<void> {
  if (!token) return;
  const device = await describeDevice();
  await axios.post('/devices/register', { token, platform: 'ios', ...device });
  registeredToken = token;
}

/**
 * Binds the notification listeners. Safe to call before login and before the
 * router exists — it requests no permission and shows no prompt. Call it as early
 * as possible: a listener bound late is a cold-start tap missed.
 */
export async function initPush(): Promise<void> {
  if (!isNative() || listenersBound) return;
  listenersBound = true;
  try {
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const link = extractDeepLink(event);
      if (link) routeDeepLink(link);
    });
    // FCM rotates tokens on its own schedule; a stale row silently stops delivering.
    await FirebaseMessaging.addListener('tokenReceived', (event) => {
      void syncToken(event?.token).catch((error) =>
        console.warn('Failed to sync rotated push token:', error)
      );
    });
  } catch (error) {
    listenersBound = false;
    console.warn('Failed to bind push listeners:', error);
  }
}

/** Current OS-level push permission, without prompting. */
export async function getPushPermission(): Promise<PushPermission> {
  if (!isNative()) return 'unsupported';
  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    return normalizePermission(receive);
  } catch (error) {
    console.warn('Failed to read push permission:', error);
    return 'unsupported';
  }
}

function readPrimingDismissedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(PRIMING_DISMISSED_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Records a "not now" so the priming step stops nagging for PRIMING_COOLDOWN_MS. */
export function dismissPriming(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(PRIMING_DISMISSED_KEY, String(now));
  } catch {
    // Private mode — worst case the user sees the priming step again next launch.
  }
}

/**
 * Whether to show the in-app priming step. False once the OS decision is made in
 * either direction: 'granted' means we are done, 'denied' means the system prompt
 * is spent and only Settings can undo it.
 */
export async function shouldPrimeForPush(now: number = Date.now()): Promise<boolean> {
  if (!isNative()) return false;
  if ((await getPushPermission()) !== 'prompt') return false;
  return isPrimingCooledDown(readPrimingDismissedAt(), now);
}

/**
 * Requests permission (if still promptable), fetches the FCM token and registers
 * it with the backend. Only ever call this from the priming step's confirm
 * button — this is the one and only system prompt the install gets.
 */
export async function enablePush(): Promise<PushRegistration> {
  if (!isNative()) return { status: 'unsupported' };

  try {
    let permission = await getPushPermission();
    if (permission === 'prompt') {
      const result = await FirebaseMessaging.requestPermissions();
      permission = normalizePermission(result?.receive);
    }
    if (permission !== 'granted') return { status: 'denied' };

    const { token } = await FirebaseMessaging.getToken();
    if (!token) return { status: 'error' };

    await syncToken(token);
    return { status: 'registered', token };
  } catch (error) {
    console.error('Failed to enable push notifications:', error);
    return { status: 'error' };
  }
}

/**
 * Removes this device's token on logout, so the next person to sign in on this
 * phone does not receive the previous user's lineups.
 *
 * MUST run before the JWT is cleared — the DELETE is authenticated.
 */
export async function disablePush(): Promise<void> {
  if (!isNative()) return;

  let token = registeredToken;
  if (!token) {
    try {
      token = (await FirebaseMessaging.getToken())?.token ?? null;
    } catch {
      token = null;
    }
  }
  if (!token) return;

  try {
    await axios.delete(`/devices/${encodeURIComponent(token)}`);
  } catch (error) {
    console.warn('Failed to unregister push token:', error);
  }
  registeredToken = null;
}
